import { useEffect, useState, useCallback } from 'react';
import browser from '@openclassrooms/utils/browser';
import { getCookies } from '@openclassrooms/utils/cookies/services';
import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import getBlueshiftLiveContent from '../services/getBlueshiftLiveContent';
import { COOKIE_NAME, SlotNames } from '../constants/blueshift';
import type { SlotNamesToContent } from '..';
import useGetMarketingStaticContent from './useGetMarketingStaticContent';
import useMaintenanceStaticContent, { SHOULD_DISPLAY_MAINTENANCE_BANNER } from './useMaintenanceStaticContent';
export default function useBlueshiftLiveContent<T extends SlotNames>(slot: T) {
  const {
    currentUser,
    isPending
  } = useCurrentUserContext();
  const [liveContent, setLiveContent] = useState<SlotNamesToContent[T] | null>(null);
  const {
    href
  } = browser.window?.location ?? {
    href: ''
  };
  const {
    slotDatas
  } = useGetMarketingStaticContent(slot);
  const {
    slotDatas: maintenanceSlotDatas
  } = useMaintenanceStaticContent(slot);
  const matchUrl = useCallback((matchWithRegExp: boolean, urlToMatch: string) => matchWithRegExp ? href.match(new RegExp(urlToMatch)) : href.includes(urlToMatch), [href]);
  const userEmail = currentUser?.email;
  const loadSlotLiveContent = useCallback(async () => {
    if (!getCookies()[COOKIE_NAME]) {
      setTimeout(loadSlotLiveContent, 100);
      return;
    }
    let slotContent: SlotNamesToContent[T];
    let slotLiveContentData: SlotNamesToContent[T] | null | undefined;
    try {
      slotLiveContentData = (await getBlueshiftLiveContent(slot, userEmail))?.data;
    } catch {
      return;
    }
    if (!slotLiveContentData || !slotLiveContentData.content) {
      return;
    }
    if (slotLiveContentData.show === false) {
      return;
    }
    const {
      content
    } = slotLiveContentData;
    if (content.matchingUrl || content.isMatchingUrlRegExp) {
      slotContent = matchUrl(content.isMatchingUrlRegExp, content.matchingUrl) ? slotLiveContentData : null;
    } else {
      slotContent = slotLiveContentData;
    }
    if (!slotContent) {
      return;
    }
    setLiveContent(slotContent);
  }, [setLiveContent, slot, matchUrl, userEmail]);
  const loadSlotStaticContent = useCallback(() => {
    let slotContent_0: SlotNamesToContent[T] | undefined;
    if (slotDatas.length > 1) {
      slotContent_0 = slotDatas.find(({
        content: content_0
      }) => matchUrl(content_0.isMatchingUrlRegExp, content_0.matchingUrl));
    } else {
      [slotContent_0] = slotDatas;
    }
    if (SHOULD_DISPLAY_MAINTENANCE_BANNER) {
      if (maintenanceSlotDatas.length > 1) {
        slotContent_0 = maintenanceSlotDatas.find(({
          content: content_1
        }) => matchUrl(content_1.isMatchingUrlRegExp, content_1.matchingUrl));
      } else if (maintenanceSlotDatas.length === 1) {
        [slotContent_0] = maintenanceSlotDatas;
      }
    }
    if (!slotContent_0 || slotContent_0?.show === false) {
      setLiveContent(null);
      return;
    }
    setLiveContent(slotContent_0);
  }, [maintenanceSlotDatas, matchUrl, slotDatas]);
  useEffect(() => {
    if (!isPending) {
      if (!SHOULD_DISPLAY_MAINTENANCE_BANNER) {
        loadSlotLiveContent();
      }
      loadSlotStaticContent();
    }
  }, [loadSlotLiveContent, loadSlotStaticContent, isPending]);
  return {
    liveContent
  };
}